import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../utils/authUtils';
import { PERMISSIONS, useCan } from '../authority/permissions';
import contentManagementImage from '../../assets/images/content_management.webp';
import programImage from '../../assets/images/program.webp';
import peopleImage from '../../assets/images/people.webp';
import emailsImage from '../../assets/images/emails.webp';
import teamImage from '../../assets/images/team.webp';
import submissionsImage from '../../assets/images/abstract_submissions.webp';

const PageContainer = styled.div`
  min-height: 80vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: transparent;
`;

const MainContent = styled.main`
  width: 100%;
  max-width: 800px;
  padding: 2rem;
`;

const DashboardTitle = styled.h1`
  font-size: 2.25rem;
  font-weight: bold;
  color: #111827;
  margin-bottom: 2rem;
  text-align: center;
`;

const LinkGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 2rem;
`;

const StyledLink = styled(Link)`
  width: 200px;
  height: 200px;
  background-image: url(${props => props.$bgImage});
  background-size: cover;
  background-position: center;
  border-radius: 0.5rem;
  overflow: hidden;
  position: relative;
  text-decoration: none;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const LinkOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.64);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const LinkText = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  text-align: center;
`;

const LinkCount = styled.p`
  margin: 0.25rem 0 0;
  font-size: 0.9rem;
  color: #111827;
  text-align: center;
`;

const AdminDashboard = () => {
  const can = useCan();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  // Open abstract submissions; stays null (tile without a number) while unknown or when the call fails.
  const [openSubmissions, setOpenSubmissions] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      await api.get('/api/protected');
      setIsLoggedIn(true);
      // The count is a staff-area route; a program editor has no reason to ask for it.
      if (can(PERMISSIONS.staffArea)) {
        try {
          const { data } = await api.get('/api/cms/submissions/open-count');
          if (typeof data?.count === 'number') setOpenSubmissions(data.count);
        } catch (countError) {
          setOpenSubmissions(null);
        }
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      setIsLoggedIn(false);
      navigate('/admin/login');
    }
  };

  if (!isLoggedIn) {
    return (
      <PageContainer>
        <MainContent>
          <Link to="/" style={{ color: '#3b82f6', textDecoration: 'none' }}>
            Return to Home
          </Link>
        </MainContent>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <MainContent>
        <DashboardTitle>Admin Dashboard</DashboardTitle>
        <LinkGrid>
          {/* A program editor holds program.area only, so the Program tile is the one tile left
              (plan §6). The rest are the staff area's, as their APIs are. */}
          {can(PERMISSIONS.staffArea) && (
            <StyledLink to="/admin/content-management" $bgImage={contentManagementImage}>
              <LinkOverlay>
                <LinkText>Content Management</LinkText>
              </LinkOverlay>
            </StyledLink>
          )}
          {can(PERMISSIONS.programArea) && (
            <StyledLink to="/admin/program" $bgImage={programImage}>
              <LinkOverlay>
                <LinkText>Program</LinkText>
              </LinkOverlay>
            </StyledLink>
          )}
          {can(PERMISSIONS.staffArea) && (
            <StyledLink to="/admin/people" $bgImage={peopleImage}>
              <LinkOverlay>
                <LinkText>People</LinkText>
              </LinkOverlay>
            </StyledLink>
          )}
          {/* The sent log; the API is the staff area's, so admins and the team both see it. */}
          {can(PERMISSIONS.staffArea) && (
            <StyledLink to="/admin/emails" $bgImage={emailsImage}>
              <LinkOverlay>
                <LinkText>E-Mails</LinkText>
              </LinkOverlay>
            </StyledLink>
          )}
          {can(PERMISSIONS.staffManage) && (
            <StyledLink to="/admin/team" $bgImage={teamImage}>
              <LinkOverlay>
                <LinkText>Team</LinkText>
              </LinkOverlay>
            </StyledLink>
          )}
          {can(PERMISSIONS.staffArea) && (
            <StyledLink to="/admin/submission-management" $bgImage={submissionsImage}>
              <LinkOverlay>
                <div>
                  <LinkText>Abstract submissions</LinkText>
                  {openSubmissions !== null && <LinkCount>{openSubmissions} open</LinkCount>}
                </div>
              </LinkOverlay>
            </StyledLink>
          )}
        </LinkGrid>
      </MainContent>
    </PageContainer>
  );
};

export default AdminDashboard;